import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import { useSheetDrag } from '../utils/useSheetDrag';

// Mobile bottom sheet hosting a page's full filter set (opened by
// FilterButton). Generalizes the PeriodPicker mobile-sheet pattern:
// portal to <body>, dim backdrop, grab handle, slide-up animation,
// safe-area footer. Mounted only on ≤720px viewports, so its CSS
// (.filter-sheet-*) needs no media query.
//
// Filters inside commit instantly (same semantics as desktop); the footer's
// "הצג תוצאות (N)" button just closes the sheet — the live count tells the
// user what they'll land on before they close.
//
// z-index 1050/1051 — deliberately BELOW the PeriodPicker sheet (1100/1101).
// The date control no longer lives in this sheet (it has its own sticky bar
// above the filters), but the ordering still matters: nothing in here may
// paint over the date picker.
export default function FilterSheet({
  open,
  onClose,
  title = 'סינון',
  resultCount,
  onClearAll,
  children,
}) {
  // Swipe down on the sheet (from the handle, or from content scrolled to its
  // top) closes it, as the grab handle promises.
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  useSheetDrag(sheetRef, onClose, { enabled: open, backdropRef });

  // Body scroll lock while open (restore the previous inline value —
  // something else may have set it before us).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div ref={backdropRef} className="filter-sheet-backdrop" onClick={onClose} aria-hidden />
      <div ref={sheetRef} className="filter-sheet" role="dialog" aria-label={title}>
        <div className="filter-sheet-handle" aria-hidden />
        <div className="filter-sheet-header">
          <span className="filter-sheet-title">{title}</span>
          <button
            type="button"
            className="docs-filter-datepop-close"
            onClick={onClose}
            aria-label="סגור"
          ><Icon name="close" size={16} strokeWidth={1.8} /></button>
        </div>
        <div className="filter-sheet-body">{children}</div>
        <div className="filter-sheet-footer">
          {onClearAll && (
            <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
              נקה הכל
            </button>
          )}
          <button type="button" className="filter-sheet-apply" onClick={onClose}>
            {typeof resultCount === 'number' ? `הצג תוצאות (${resultCount})` : 'סגור'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
